import { useCallback, useEffect, useState } from 'react';
import { useCheckoutApi } from '@app/react-common/api/useCheckoutApi';
import { useAppContext } from '@app/react-common/AppContext';

export interface CheckoutProfile {
  id: number;
  name: string;
}

export const CHECKOUT_PROFILE_ENDPOINT = '/admin/v1/shops/checkout-profiles';

const useCheckoutProfiles = (shouldFetch: boolean = true) => {
  const [checkoutProfiles, setCheckoutProfiles] = useState<CheckoutProfile[]>([]);
  const [loading, setLoading] = useState<boolean>(shouldFetch);
  const { showToast } = useAppContext();

  const failureHandler = useCallback(() => {
    showToast({
      message: 'Failed to load checkout profiles. Please contact support if the problem persists.',
      mode: 'danger',
    });
  }, [showToast]);

  const checkoutApi = useCheckoutApi(failureHandler);

  useEffect(() => {
    if (!shouldFetch) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    checkoutApi
      .get<CheckoutProfile[]>(CHECKOUT_PROFILE_ENDPOINT)
      .then((response) => {
        if (!cancelled) setCheckoutProfiles(response.data ?? []);
      })
      .catch(() => {
        if (!cancelled) setCheckoutProfiles([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [shouldFetch]);

  return { checkoutProfiles, loading };
};

export default useCheckoutProfiles;
